import { sortAsc } from '@strata/helper-functions';
import { DialogWithForm } from '@strata/react-dialog';
import { SuggestMultiSelectFormik } from '@strata/react-selects';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import * as Yup from 'yup';

import { handleError } from 'services/ErrorManagement';
import { put, useGet } from 'services/Http';

export const UpdateDiagnosesDialog = ({
    isOpen,
    fetchPolicy,
    onSecondaryButtonClick,
    diagnoses = undefined,
    payerId,
    draftId,
}) => {
    const diagnosesData = useGet(isOpen ? '/reference/diagnoses/' : null);
    const diagnosesList = useMemo(
        () =>
            diagnosesData
                ?.map((dx) => ({
                    name: dx.description,
                    secondaryData: dx.oid,
                }))
                .sort((a, b) => sortAsc(a.name, b.name)),
        [diagnosesData],
    );

    const initialDiagnosesValues =
        diagnoses && diagnosesList
            ? diagnoses.map(
                  (oid) =>
                      diagnosesList.find((option) => option.secondaryData === oid) ?? {
                          name: oid,
                          secondaryData: oid,
                      },
              )
            : [];
    return (
        <Formik
            initialValues={{
                diagnoses: initialDiagnosesValues,
            }}
            enableReinitialize
            validationSchema={Yup.object({
                diagnoses: Yup.array().min(1, validationStrings.requiredField.errorMessage),
            })}
            onSubmit={async (values, { resetForm }) => {
                try {
                    await put(`/clinicalpolicy/admin/${payerId}/drafts/${draftId}/dxs`, {
                        dxs: values.diagnoses.map((dx) => dx.secondaryData).sort(sortAsc),
                    });
                    await fetchPolicy();
                    resetForm();
                    onSecondaryButtonClick();
                } catch (err) {
                    handleError('Error updating diagnoses.', err);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle="Update Diagnoses"
                    primaryButtonLabel="Save"
                    secondaryButtonLabel="Cancel"
                    isOpen={isOpen}
                    onSecondaryButtonClick={() => {
                        formik.handleReset();
                        onSecondaryButtonClick();
                    }}
                    isPrimaryButtonDisabled={
                        !(formik.dirty && formik.isValid) || formik.isSubmitting
                    }
                    dialogContent={
                        <SuggestMultiSelectFormik
                            id="diagnoses"
                            label="Diagnoses"
                            required
                            isLoading={!diagnosesList}
                            values={diagnosesList}
                        />
                    }
                />
            )}
        </Formik>
    );
};

UpdateDiagnosesDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    fetchPolicy: PropTypes.func.isRequired,
    onSecondaryButtonClick: PropTypes.func.isRequired,
    diagnoses: PropTypes.arrayOf(PropTypes.string),
    payerId: PropTypes.string.isRequired,
    draftId: PropTypes.string.isRequired,
};
